/*
  IA Tabs — <ia-tabs-component>. Two ways to author tabs:

  1. Explicit markup (original API): provide your own triggers + panels.
  <ia-tabs-component class="ia-tabs">
    <div class="ia-tabs__header">
      <button class="ia-tab ia-tab--active" data-tab-index="0">Tab 1</button>
      <button class="ia-tab" data-tab-index="1">Tab 2</button>
    </div>
    <div class="ia-tabs__content">
      <div class="ia-tab-detail is-active" data-detail-index="0">Content 1</div>
      <div class="ia-tab-detail" data-detail-index="1">Content 2</div>
    </div>
  </ia-tabs-component>

  2. Label mode (auto headers): panels only — each carries data-tab-label;
     the component builds the header row itself. Made for nested theme blocks
     ({% content_for 'blocks' %}), where the parent template can't read child
     settings. data-tab-class on the component sets the typography classes of
     the generated buttons (default: ia-button-sm).
  <ia-tabs-component class="ia-tabs" data-tab-class="ia-button-sm">
    <div class="ia-tabs__content">
      <div class="ia-tab-detail" data-tab-label="Tab 1">Content 1</div>
      <div class="ia-tab-detail" data-tab-label="Tab 2">Content 2</div>
    </div>
  </ia-tabs-component>

  Trigger N pairs with panel N by DOM order (the data-tab-index / data-detail-index
  values are not read). Active classes: .ia-tab--active (trigger), .is-active (panel).
  In the theme editor, selecting a block inside a panel reveals that panel.
*/

const span = (className, text) => {
  const node = document.createElement('span')
  node.className = className
  node.textContent = text
  return node
}

if (!customElements.get('ia-tabs-component')) {
  class IaTabsComponent extends HTMLElement {
    static uidCounter = 0

    connectedCallback() {
      this.buildHeaderFromLabels()

      this.tabs = Array.from(this.querySelectorAll('[data-tab-index]'))
      this.details = Array.from(this.querySelectorAll('[data-detail-index], [data-tab-label]'))
      if (!this.tabs.length || !this.details.length) return

      this.setupA11y()

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index))
        tab.addEventListener('keydown', this.handleKeydown.bind(this, index))
      })

      const activeIndex = Math.max(0, this.details.findIndex((detail) => detail.classList.contains('is-active')))
      this.select(activeIndex, { focus: false, wake: false })

      this.handleBlockSelect = this.handleBlockSelect.bind(this)
      document.addEventListener('shopify:block:select', this.handleBlockSelect)
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.handleBlockSelect)
    }

    /* Label mode: generate the trigger row from the panels' data-tab-label. */
    buildHeaderFromLabels() {
      if (this.querySelector('[data-tab-index]')) return
      const panels = Array.from(this.querySelectorAll('[data-tab-label]'))
      if (!panels.length) return

      const header = document.createElement('div')
      header.className = 'ia-tabs__header'

      const tabClass = this.dataset.tabClass || 'ia-button-sm'
      panels.forEach((panel, index) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = `ia-tab ${tabClass}`.trim()
        button.dataset.tabIndex = index
        this.fillLabel(button, panel, index)
        this.fillTrackingId(button, panel)
        header.appendChild(button)
        panel.classList.add('ia-tab-detail')
      })

      this.prepend(header)
    }

    /**
     * A trigger's text, in one or two viewports. With no mobile label the
     * desktop one stands alone. When the mobile label is a slice of the desktop
     * one ("Scents" inside "Iconic SDJ scents"), the trigger keeps the full
     * text and wraps only the parts the short version drops, so the two read as
     * one label trimmed rather than two labels swapped. Otherwise both are
     * rendered and CSS shows the one that belongs to the viewport.
     */
    fillLabel(button, panel, index) {
      const full = panel.dataset.tabLabel || `Tab ${index + 1}`
      const short = (panel.dataset.tabLabelMobile || '').trim()
      if (!short || short.toLowerCase() === full.toLowerCase()) {
        button.textContent = full
        return
      }

      const at = full.toLowerCase().indexOf(short.toLowerCase())
      if (at === -1) {
        button.append(span('ia-tab__full', full), span('ia-tab__short', short))
        return
      }
      const before = full.slice(0, at)
      const kept = full.slice(at, at + short.length)
      const after = full.slice(at + short.length)
      if (before) button.append(span('ia-tab__trim', before))
      button.append(document.createTextNode(kept))
      if (after) button.append(span('ia-tab__trim', after))
    }

    /**
     * A stable id for the analytics tag (data-cs-override-id): the generated
     * tab ids follow DOM order, so a reordered tab would change meaning in a
     * report. The panel's own tracking id wins, else the label as a slug,
     * both under the component's prefix; no prefix, no attribute.
     */
    fillTrackingId(button, panel) {
      const prefix = (this.dataset.csPrefix || '').trim()
      if (!prefix) return
      const own = (panel.dataset.csId || '').trim()
      const label = panel.dataset.tabLabel || ''
      const slug = (own || label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      if (slug) button.dataset.csOverrideId = `${prefix}-${slug}`
    }

    setupA11y() {
      const header = this.tabs[0].closest('.ia-tabs__header') || this.tabs[0].parentElement
      header?.setAttribute('role', 'tablist')

      const uid = ++IaTabsComponent.uidCounter
      this.tabs.forEach((tab, index) => {
        const panel = this.details[index]
        if (!panel) return
        if (!tab.id) tab.id = `ia-tab-${uid}-${index}`
        if (!panel.id) panel.id = `ia-tabpanel-${uid}-${index}`
        tab.setAttribute('role', 'tab')
        tab.setAttribute('aria-controls', panel.id)
        panel.setAttribute('role', 'tabpanel')
        panel.setAttribute('aria-labelledby', tab.id)
      })
    }

    select(index, { focus = true, wake = true } = {}) {
      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index
        tab.classList.toggle('ia-tab--active', isActive)
        tab.classList.toggle('is-active', isActive)
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
        tab.setAttribute('tabindex', isActive ? '0' : '-1')
      })
      this.details.forEach((detail, detailIndex) => {
        const isActive = detailIndex === index
        detail.classList.toggle('is-active', isActive)
        if (isActive && wake) this.wake(detail)
      })
      if (focus) this.tabs[index]?.focus()
    }

    /*
      A lazy image inside a hidden panel is deferred, and the browser does not
      look at it again when the panel is revealed: measured 2026-08-14, opening
      a tab left every image in it unfetched, through clicks and scrolling
      both. So the panel that just opened stops waiting.

      Not on the first paint though: the panel that starts open is visible, so
      the browser fetches its lazy images by itself when the section nears the
      viewport. Waking them at init turned every first panel on the page eager,
      tabs far below the fold included.
    */
    wake(panel) {
      panel.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        if (!image.complete || !image.naturalWidth) image.loading = 'eager'
      })
    }

    handleKeydown(index, event) {
      const last = this.tabs.length - 1
      let next = null
      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1
      if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1
      if (event.key === 'Home') next = 0
      if (event.key === 'End') next = last
      if (next === null) return
      event.preventDefault()
      this.select(next)
    }

    /* Theme editor: reveal the panel that holds the selected block. */
    handleBlockSelect(event) {
      if (!this.contains(event.target)) return
      const index = this.details.findIndex((detail) => detail === event.target || detail.contains(event.target))
      if (index >= 0) this.select(index, { focus: false })
    }
  }

  customElements.define('ia-tabs-component', IaTabsComponent)
}
